/**
 * VoiceRecordButton Component
 *
 * Presentational microphone button with three visual states:
 * - Idle: gray mic icon
 * - Recording: pulsing red dot + mm:ss timer + stop button
 * - Transcribing: spinning loader
 *
 * Voice state is owned by the parent via `useVoiceRecorder`. The component
 * is namespace-agnostic — consumers pass labels through the `labels` prop
 * so the same button works for chatbot, agent runner, or any future surface
 * without coupling to a specific i18n namespace.
 *
 * @module VoiceRecordButton
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { IconButton, HStack, Box, Text } from '@axon/ui';
import { LuMic, LuSquare, LuLoader, LuX } from 'react-icons/lu';

import type { UseVoiceRecorderReturn, VoiceErrorKey } from './useVoiceRecorder';

// ============================================================================
// Constants
// ============================================================================

/** Duration in ms to show error message before auto-clearing */
const ERROR_DISPLAY_DURATION_MS = 4000;

// ============================================================================
// Types
// ============================================================================

/**
 * Display strings for the voice button — passed in by the consumer so the
 * component stays namespace-agnostic. Build via the consumer's own
 * `useTranslations(<namespace>)` and pass as a prop.
 */
export interface VoiceLabels {
  stopRecording: string;
  cancelRecording: string;
  transcribing: string;
  tooltip: string;
  errors: Record<VoiceErrorKey, string>;
}

interface VoiceRecordButtonProps {
  /** Voice recorder state and controls (from useVoiceRecorder) */
  voice: UseVoiceRecorderReturn;
  /** Whether the surrounding surface is ready to accept input */
  isConnected: boolean;
  /** Display labels (i18n-resolved by the consumer) */
  labels: VoiceLabels;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Format seconds as mm:ss
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Presentational voice record button. All voice state is owned by the
 * parent via `useVoiceRecorder`; all i18n strings come in via `labels`.
 *
 * @example
 * const t = useTranslations('agentRunner');
 * const labels: VoiceLabels = {
 *   stopRecording: t('voice.stopRecording'),
 *   cancelRecording: t('voice.cancelRecording'),
 *   transcribing: t('voice.transcribing'),
 *   tooltip: t('voice.tooltip'),
 *   errors: {
 *     notSupported: t('voice.notSupported'),
 *     permissionDenied: t('voice.permissionDenied'),
 *     transcriptionFailed: t('voice.transcriptionFailed'),
 *     noSpeechDetected: t('voice.noSpeechDetected'),
 *   },
 * };
 * <VoiceRecordButton voice={voiceRecorder} isConnected={true} labels={labels} />
 */
export function VoiceRecordButton({ voice, isConnected, labels }: VoiceRecordButtonProps) {
  const {
    state,
    isRecording,
    isTranscribing,
    duration,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  } = voice;

  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showError, setShowError] = useState<VoiceErrorKey | null>(null);

  // Always clear before scheduling so a rapid error → error transition
  // can't leave the previous timer alive and clear `showError` mid-flight.
  useEffect(() => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    if (!error) return;
    setShowError(error);
    errorTimerRef.current = setTimeout(() => setShowError(null), ERROR_DISPLAY_DURATION_MS);
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [error]);

  const handleClick = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else if (state === 'idle') {
      startRecording();
    }
  }, [isRecording, state, startRecording, stopRecording]);

  // Recording state — show stop button + timer
  if (isRecording) {
    return (
      <HStack gap="1" align="center">
        {/* Pulsing red dot */}
        <Box
          width="2"
          height="2"
          borderRadius="full"
          bg="red.500"
          css={{
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
        <Text fontSize="xs" color="red.500" fontFamily="mono" minWidth="8">
          {formatDuration(duration)}
        </Text>
        <IconButton
          aria-label={labels.stopRecording}
          icon={<LuSquare size={14} />}
          onClick={stopRecording}
          size="sm"
          colorPalette="red"
          variant="ghost"
        />
        <IconButton
          aria-label={labels.cancelRecording}
          icon={<LuX size={14} />}
          onClick={cancelRecording}
          size="sm"
          variant="ghost"
        />
      </HStack>
    );
  }

  // Transcribing state — show spinner
  if (isTranscribing) {
    return (
      <IconButton
        aria-label={labels.transcribing}
        icon={<LuLoader className="animate-spin" size={18} />}
        disabled
        size="md"
      />
    );
  }

  return (
    <HStack gap="1" align="center">
      {showError && (
        <Text fontSize="xs" color="red.500" maxWidth="36" lineClamp={2}>
          {labels.errors[showError]}
        </Text>
      )}
      <IconButton
        aria-label={labels.tooltip}
        icon={<LuMic size={18} />}
        onClick={handleClick}
        disabled={!isConnected}
        size="md"
        variant="ghost"
        title={labels.tooltip}
      />
    </HStack>
  );
}

export default VoiceRecordButton;
